import type { AuthService, AuthUser, Unsubscribe } from '@mendikot/app-core';
import type { Reply } from '@mendikot/protocol';
import type { FirebaseAuthService } from './auth';
import { loadAuth } from './firebase';

/**
 * AuthService facade over the lazily loaded Firebase Auth SDK. `current()` is null until the SDK has
 * restored the session; listeners are told as soon as it has. Nothing loads (Auth, App Check and its
 * reCAPTCHA) until first use, so a new visitor sees the privacy notice before any of it runs.
 */
export class LazyAuthService implements AuthService {
  readonly available = true;
  private impl: FirebaseAuthService | null = null;
  private user: AuthUser | null = null;
  private readonly listeners = new Set<(u: AuthUser | null) => void>();
  private loading: Promise<FirebaseAuthService> | null = null;

  constructor(private readonly mergeGuest: (guestIdToken: string) => Promise<Reply>) {}

  private ready(): Promise<FirebaseAuthService> {
    this.loading ??= Promise.all([loadAuth(), import('./auth')]).then(([auth, mod]) => {
      const impl = new mod.FirebaseAuthService(auth, this.mergeGuest);
      impl.onChange((user) => {
        this.user = user;
        for (const listener of this.listeners) listener(user);
      });
      this.impl = impl;
      return impl;
    });
    return this.loading;
  }

  current(): AuthUser | null {
    return this.user;
  }

  onChange(listener: (user: AuthUser | null) => void): Unsubscribe {
    this.listeners.add(listener);
    if (this.impl) listener(this.user);
    return () => this.listeners.delete(listener);
  }

  ensureSignedIn = async () => (await this.ready()).ensureSignedIn();
  signInWithGoogle = async () => (await this.ready()).signInWithGoogle();
  signInWithEmail = async (email: string, password: string) =>
    (await this.ready()).signInWithEmail(email, password);
  signUpWithEmail = async (email: string, password: string, name: string) =>
    (await this.ready()).signUpWithEmail(email, password, name);
  sendPasswordReset = async (email: string) => (await this.ready()).sendPasswordReset(email);
  signOut = async () => (await this.ready()).signOut();
  idToken = async () => (await this.ready()).idToken();
}
